import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { FeaturedVideoProperty } from '~/core/entities/property';

import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { defineStore } from 'pinia';

export interface FeaturedVideo {
	property: FeaturedVideoProperty;
	videoUrl: string;
	thumbnailUrl: string | null;
	videoWidth?: number;
	videoHeight?: number;
}

export interface VideoFilters {
	cities?: string[];
	macroregions?: string[];
	regions?: string[];
	propertyTypes?: PropertyTypeEnum[];
	minAskingPrice?: number;
	maxAskingPrice?: number;
	minArea?: number;
	maxArea?: number;
	bedrooms?: number;
	suites?: number;
	bathrooms?: number;
	parkingSpots?: number;
	features?: string[];
}

interface FetchResult {
	items: FeaturedVideo[];
	hasMore: boolean;
	allWatched?: boolean;
	totalAvailable?: number;
}

const PER_PAGE = 8;

function propertyToFeaturedVideo(
	property: FeaturedVideoProperty
): FeaturedVideo | null {
	const featuredVideo = property.featuredVideos?.[0];
	if (!featuredVideo) return null;

	return {
		property,
		videoUrl: featuredVideo.url,
		thumbnailUrl: featuredVideo.thumbnail ?? null,
		videoWidth: featuredVideo.width,
		videoHeight: featuredVideo.height
	};
}

export const useFeaturedVideosStore = defineStore('featuredVideos', () => {
	const { user } = useUserSession();
	const getUserId = () => {
		if (!import.meta.client) return undefined;
		return user.value?.id;
	};

	const getCookieData = useCookieData();

	const videos = shallowRef<FeaturedVideo[]>([]);
	const isLoading = shallowRef(false);
	const isPreloaded = shallowRef(false);
	const hasMore = shallowRef(true);
	const currentPage = shallowRef(1);
	const activeFilters = shallowRef<VideoFilters>({});
	const allWatched = shallowRef(false);
	const totalAvailable = shallowRef(0);

	async function fetchVideos(
		page: number,
		perPage: number = PER_PAGE,
		filters: VideoFilters = {}
	): Promise<FetchResult> {
		const [result, error] = await nuxtPropertiesRepository.getFeaturedVideos(
			{
				markUserWishlistsId: getUserId(),
				page,
				perPage,
				cities: filters.cities,
				macroregions: filters.macroregions,
				regions: filters.regions,
				propertyTypes: filters.propertyTypes,
				minAskingPrice: filters.minAskingPrice,
				maxAskingPrice: filters.maxAskingPrice,
				minArea: filters.minArea,
				maxArea: filters.maxArea,
				bedrooms: filters.bedrooms,
				suites: filters.suites,
				bathrooms: filters.bathrooms,
				parkingSpots: filters.parkingSpots,
				features: filters.features
			},
			getCookieData()
		);

		if (error || !result) {
			return { items: [], hasMore: false };
		}

		const items = (result.properties ?? [])
			.map(propertyToFeaturedVideo)
			.filter((item): item is FeaturedVideo => item !== null);

		// Support both snake_case (from API) and camelCase formats
		const pagination = result.pagination as unknown as Record<string, number>;
		const totalPages = pagination?.totalPages ?? pagination?.total_pages ?? 1;
		const hasMorePages = (pagination?.page ?? 1) < totalPages;

		return {
			items,
			hasMore: hasMorePages,
			allWatched: result.allWatched,
			totalAvailable: result.totalAvailable
		};
	}

	async function preload(filters: VideoFilters = {}): Promise<void> {
		if (isPreloaded.value || isLoading.value) return;

		isLoading.value = true;
		activeFilters.value = filters;

		try {
			const result = await fetchVideos(1, PER_PAGE, filters);
			videos.value = result.items;
			hasMore.value = result.hasMore;
			allWatched.value = result.allWatched ?? false;
			totalAvailable.value = result.totalAvailable ?? 0;
			currentPage.value = 1;
			isPreloaded.value = true;
		} finally {
			isLoading.value = false;
		}
	}

	async function loadMore(): Promise<void> {
		if (isLoading.value || !hasMore.value) return;

		isLoading.value = true;

		try {
			const nextPage = currentPage.value + 1;
			const result = await fetchVideos(nextPage, PER_PAGE, activeFilters.value);
			videos.value = [...videos.value, ...result.items];
			hasMore.value = result.hasMore;
			allWatched.value = result.allWatched ?? allWatched.value;
			totalAvailable.value = result.totalAvailable ?? totalAvailable.value;
			currentPage.value = nextPage;
		} finally {
			isLoading.value = false;
		}
	}

	async function applyFilters(filters: VideoFilters): Promise<void> {
		activeFilters.value = filters;
		isPreloaded.value = false;
		videos.value = [];
		currentPage.value = 1;
		hasMore.value = true;
		allWatched.value = false;
		totalAvailable.value = 0;

		await preload(filters);
	}

	function reset(): void {
		videos.value = [];
		isLoading.value = false;
		isPreloaded.value = false;
		hasMore.value = true;
		currentPage.value = 1;
		activeFilters.value = {};
		allWatched.value = false;
		totalAvailable.value = 0;
	}

	return {
		videos,
		isLoading,
		isPreloaded,
		hasMore,
		currentPage,
		activeFilters,
		allWatched,
		totalAvailable,
		preload,
		loadMore,
		applyFilters,
		reset
	};
});
